import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CImage,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft, cilCart, cilCheck } from '@coreui/icons'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import EmptyStatePanel from '../../components/EmptyStatePanel'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'

const attachAbortListeners = (upstreamSignal, controller) => {
  if (!upstreamSignal) return () => {}

  if (upstreamSignal.aborted) {
    controller.abort()
    return () => {}
  }

  const forwardAbort = () => controller.abort()
  upstreamSignal.addEventListener('abort', forwardAbort)
  controller.signal.addEventListener(
    'abort',
    () => upstreamSignal.removeEventListener('abort', forwardAbort),
    { once: true },
  )

  return () => upstreamSignal.removeEventListener('abort', forwardAbort)
}

const EshopBought = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [accountId] = useState(() => localStorage.getItem('account_ID') || '')
  const [membership] = useState(() => localStorage.getItem('membership'))
  const [email] = useState(() => localStorage.getItem('mail') || '')

  const [accounts, setAccounts] = useState([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [accountsError, setAccountsError] = useState(null)

  const [items, setItems] = useState([])
  const itemsRef = useRef(items)
  const [itemsLoading, setItemsLoading] = useState(false)
  const [itemsError, setItemsError] = useState(null)

  const [actionFeedback, setActionFeedback] = useState(null)

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    if (!actionFeedback) return
    const timeout = setTimeout(() => setActionFeedback(null), 3500)
    return () => clearTimeout(timeout)
  }, [actionFeedback])

  const isCanceledError = useCallback(
    (error) =>
      apiClient.isCancel?.(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError',
    [],
  )

  const fetchAccountsData = useCallback(
    async ({ signal } = {}) => {
      if (!email) {
        setAccounts([])
        return
      }

      setAccountsLoading(true)
      setAccountsError(null)
      try {
        const response = await apiClient.get(buildApiUrl('account'), {
          params: { email },
          signal,
        })
        setAccounts(Array.isArray(response.data) ? response.data : [])
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error fetching account information', error)
        setAccountsError(t('eshopBought.errors.accounts'))
      } finally {
        setAccountsLoading(false)
      }
    },
    [email, isCanceledError, t],
  )

  const itemsFetchInFlight = useRef(null)

  const fetchItemsData = useCallback(
    async ({ signal, skipLoading = false } = {}) => {
      if (!accountId) {
        itemsRef.current = []
        setItems([])
        return
      }

      if (itemsFetchInFlight.current) {
        itemsFetchInFlight.current.abort()
      }

      const controller = new AbortController()
      const detachAbort = attachAbortListeners(signal, controller)

      itemsFetchInFlight.current = controller

      if (!skipLoading) {
        setItemsLoading(true)
      }
      setItemsError(null)
      try {
        const response = await apiClient.get(buildApiUrl('eshopBought'), {
          params: { account_ID: accountId },
          signal: controller.signal,
        })
        const nextItems = Array.isArray(response.data) ? response.data : []
        itemsRef.current = nextItems
        setItems(nextItems)
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error fetching bought items', error)
        setItemsError(t('eshopBought.errors.items'))
      } finally {
        if (!skipLoading) {
          setItemsLoading(false)
        }
        detachAbort()
        if (itemsFetchInFlight.current === controller) {
          itemsFetchInFlight.current = null
        }
      }
    },
    [accountId, isCanceledError, t],
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchAccountsData({ signal: controller.signal })

    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
        console.warn('Unable to hide splash screen on bought list load', splashError)
      })
    }

    return () => controller.abort()
  }, [fetchAccountsData])

  useEffect(() => {
    const controller = new AbortController()
    fetchItemsData({ signal: controller.signal })

    return () => {
      controller.abort()
      if (itemsFetchInFlight.current) {
        itemsFetchInFlight.current.abort()
        itemsFetchInFlight.current = null
      }
    }
  }, [fetchItemsData])

  const getComparableId = useCallback((item) => String(item?.cart_item_id ?? item?.item_id), [])

  const handleItemStatusChange = useCallback(
    async (cartItemId) => {
      if (!cartItemId) return

      setActionFeedback(null)
      const previousItems = itemsRef.current
      setItems((previousItemsState) => {
        const filtered = previousItemsState.filter(
          (existingItem) => getComparableId(existingItem) !== String(cartItemId),
        )
        itemsRef.current = filtered
        return filtered
      })

      try {
        await apiClient.get(buildApiUrl('eshopItemStatus'), {
          params: { status: 'NULL', item_id: cartItemId },
        })
        setActionFeedback({ type: 'success', message: t('eshopBought.feedback.itemReturned') })
        fetchItemsData({ skipLoading: true }).catch((fetchError) => {
          if (!isCanceledError(fetchError)) {
            console.error('Error refreshing bought items', fetchError)
          }
        })
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error updating bought item status', error)
        itemsRef.current = previousItems
        setItems(previousItems)
        setActionFeedback({ type: 'error', message: t('eshopBought.feedback.itemReturnError') })
      }
    },
    [fetchItemsData, getComparableId, isCanceledError, t],
  )

  const primaryAccount = accounts[0]
  const accountLabel =
    primaryAccount?.name || primaryAccount?.email || primaryAccount?.login || email || null

  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader title={t('eshopBought.header.title')} onBack={() => navigate(-1)} />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="border-0 shadow-sm">
              <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
                {actionFeedback &&
                  (actionFeedback.type === 'error' ? (
                    <CAlert
                      color="danger"
                      className="mb-0"
                      dismissible
                      onClose={() => setActionFeedback(null)}
                    >
                      {actionFeedback.message}
                    </CAlert>
                  ) : (
                    <div className="alert-panel d-flex align-items-start gap-3 rounded-3 shadow-sm bg-white bg-opacity-75 p-3 mb-0 border border-success border-opacity-25">
                      <span
                        className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm flex-shrink-0"
                        style={{
                          width: '26px',
                          height: '26px',
                          background: 'linear-gradient(135deg, #16a34a, #22c55e)',
                          color: '#ffffff',
                        }}
                        aria-hidden="true"
                        role="img"
                      >
                        <CIcon icon={cilCheck} size="sm" />
                      </span>
                      <div className="flex-grow-1">
                        <small
                          className="text-uppercase fw-semibold"
                          style={{ fontSize: '0.6rem', color: '#15803d' }}
                        >
                          {t('eshopBought.feedback.successLabel')}
                        </small>
                        <span
                          className="text-muted"
                          style={{
                            fontSize: '0.68rem',
                            color: '#14532d',
                            display: 'block',
                            marginTop: '0.25rem',
                          }}
                        >
                          {actionFeedback.message}
                        </span>
                      </div>
                      <CButton
                        color="link"
                        className="p-0 text-decoration-none text-muted flex-shrink-0"
                        onClick={() => setActionFeedback(null)}
                        aria-label={t('eshopBought.feedback.dismiss')}
                      >
                        ×
                      </CButton>
                    </div>
                  ))}

                {itemsError && (
                  <CAlert
                    color="danger"
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>{itemsError}</span>
                    <CButton
                      color="danger"
                      variant="outline"
                      size="sm"
                      onClick={() => fetchItemsData()}
                    >
                      {t('eshopBought.actions.retry')}
                    </CButton>
                  </CAlert>
                )}

                <CTable align="middle" className="mb-0 buying-list-table" hover responsive>
                  <CTableHead color="light">
                    <CTableRow>
                      <CTableHeaderCell className="text-center text-md-start"></CTableHeaderCell>
                      <CTableHeaderCell className="text-center">
                        {t('eshopBought.table.headers.action')}
                      </CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {itemsLoading ? (
                      <CTableRow>
                        <CTableDataCell colSpan={2} className="text-center">
                          <CSpinner size="sm" className="me-2" /> {t('eshopBought.table.loading')}
                        </CTableDataCell>
                      </CTableRow>
                    ) : items.length > 0 ? (
                      items.map((item) => {
                        const cartItemId = item?.cart_item_id ?? item?.item_id
                        if (cartItemId == null) {
                          console.warn('Skipping bought item without identifier', item)
                          return null
                        }

                        const membershipMatches =
                          String(item?.membership_name) === String(membership)
                        const rowKey = String(cartItemId)
                        const itemCellClass = `buying-list-item-cell${
                          membershipMatches ? '' : ' is-disabled'
                        }`

                        return (
                          <CTableRow key={rowKey} className="buying-list-row">
                            <CTableDataCell
                              color={membershipMatches ? 'secondary' : 'white'}
                              className={itemCellClass}
                            >
                              <div className="d-flex align-items-center gap-3 buying-list-item">
                                {item?.pic ? (
                                  <CImage
                                    rounded
                                    orientation="top"
                                    src={item.pic}
                                    width={64}
                                    height={64}
                                    className="buying-list-thumb"
                                  />
                                ) : (
                                  <div className="buying-list-thumb placeholder">
                                    {t('eshopBought.table.noImage')}
                                  </div>
                                )}
                                <div className="text-start">
                                  <div className="fw-semibold">{item?.name}</div>
                                  <div className="small text-medium-emphasis">{item?.category}</div>
                                  {!membershipMatches && item?.membership_name && (
                                    <div className="small text-warning mt-1">
                                      {item?.membership_name}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CTableDataCell>
                            <CTableDataCell
                              data-label={t('eshopBought.table.headers.action')}
                              className="text-center"
                              color="secondary"
                            >
                              <CButton
                                color="primary"
                                className="buying-list-actions__button buying-list-actions__button--secondary"
                                onClick={() => handleItemStatusChange(cartItemId)}
                                aria-label={t('eshopBought.actions.moveBackAria')}
                              >
                                <CIcon icon={cilArrowLeft} size="lg" />
                              </CButton>
                            </CTableDataCell>
                          </CTableRow>
                        )
                      })
                    ) : !itemsError ? (
                      <CTableRow>
                        <CTableDataCell colSpan={2}>
                          <EmptyStatePanel
                            icon={cilCart}
                            title={t('eshopBought.empty.title')}
                            message={t('eshopBought.empty.message')}
                          />
                        </CTableDataCell>
                      </CTableRow>
                    ) : null}
                  </CTableBody>
                </CTable>

                <div className="small text-medium-emphasis">
                  {accountLabel && (
                    <span>{t('eshopBought.accountInfo.label', { account: accountLabel })}</span>
                  )}
                  {accountsLoading && (
                    <span className={accountLabel ? 'ms-2' : undefined}>
                      {t('eshopBought.accountInfo.loading')}
                    </span>
                  )}
                  {accountsError && <span className="text-danger ms-2">{accountsError}</span>}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default EshopBought
